import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Biz360Logo } from "../components/layout/Biz360Logo";
import { PublicFooter } from "../components/layout/PublicFooter";

export const PolitiqueConfidentialitePage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="border-b">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Biz360Logo />
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
            data-testid="back-home-btn"
          >
            <ArrowLeft size={16} /> Retour
          </button>
        </div>
      </header>

      <main className="flex-1 max-w-3xl w-full mx-auto px-6 py-10" data-testid="privacy-page">
        <h1 className="text-3xl font-bold mb-2" style={{ color: "#210875" }}>
          Politique de confidentialité
        </h1>
        <p className="text-sm text-muted-foreground mb-8">Dernière mise à jour : 8 mai 2026</p>

        <p className="text-sm text-muted-foreground mb-8 leading-relaxed">
          La présente politique de confidentialité décrit la manière dont Biz360, application web
          de pilotage de gestion pour auto-entrepreneurs, collecte, utilise et protège les données
          personnelles de ses utilisateurs, conformément au Règlement Général sur la Protection des
          Données (RGPD - UE 2016/679) et à la loi Informatique et Libertés.
        </p>

        <Section title="Article 1 — Responsable du traitement">
          <p>
            Le responsable du traitement des données à caractère personnel collectées via
            l'application Biz360 est :
          </p>
          <ul>
            <li>
              <strong>Nom :</strong> Tiphaine SAMBLAT
            </li>
            <li>
              <strong>Statut :</strong> Auto-entrepreneur (entrepreneur individuel)
            </li>
            <li>
              <strong>SIREN :</strong> 913 345 682
            </li>
            <li>
              <strong>SIRET :</strong> 913 345 682 00037
            </li>
            <li>
              <strong>Adresse postale :</strong> indiquée dans nos{" "}
              <a
                href="/mentions-legales-cgu"
                className="underline"
                style={{ color: "#210875" }}
              >
                mentions légales
              </a>
            </li>
            <li>
              <strong>Contact :</strong>{" "}
              <a
                href="mailto:t.samblat@gmail.com"
                className="underline"
                style={{ color: "#210875" }}
              >
                t.samblat@gmail.com
              </a>
            </li>
          </ul>
        </Section>

        <Section title="Article 2 — Données collectées">
          <p>
            Dans le cadre de l'utilisation de l'application Biz360, nous collectons les données
            suivantes :
          </p>
          <p>
            <strong>2.1 Données d'identification et de compte</strong>
          </p>
          <ul>
            <li>Adresse e-mail</li>
            <li>Nom et prénom (facultatif)</li>
            <li>Mot de passe (stocké sous forme chiffrée — bcrypt)</li>
          </ul>
          <p>
            <strong>2.2 Données relatives à l'activité professionnelle</strong>
          </p>
          <ul>
            <li>Chiffre d'affaires déclaré par l'utilisateur</li>
            <li>Données de ventes et de dépenses saisies</li>
            <li>Informations clients (noms, coordonnées) saisies pour les relances</li>
            <li>SIRET de l'utilisateur, type d'activité, ACRE, versement libératoire (si renseignés)</li>
          </ul>
          <p>
            <strong>2.3 Données de navigation</strong>
          </p>
          <ul>
            <li>Adresse IP</li>
            <li>Type de navigateur et système d'exploitation</li>
            <li>Pages consultées et durée des sessions</li>
            <li>Données de tracking marketing (UTM) en cas de venue depuis une campagne publicitaire</li>
          </ul>
        </Section>

        <Section title="Article 3 — Finalités et base légale du traitement">
          <p>Les données collectées sont traitées pour les finalités suivantes :</p>
          <ul>
            <li>
              Création et gestion du compte utilisateur (base légale : exécution du contrat)
            </li>
            <li>
              Fourniture des fonctionnalités de l'application : suivi des ventes, alertes Urssaf,
              calcul des cotisations, relances clients (base légale : exécution du contrat)
            </li>
            <li>
              Envoi de notifications et alertes liées à l'activité de l'utilisateur (base légale :
              exécution du contrat)
            </li>
            <li>
              Amélioration de l'application et analyse des usages (base légale : intérêt légitime)
            </li>
            <li>
              Mesure d'audience et de performance des campagnes publicitaires (base légale :
              consentement — voir Article 8)
            </li>
            <li>
              Envoi d'e-mails d'information sur Biz360 (base légale : consentement)
            </li>
          </ul>
        </Section>

        <Section title="Article 4 — Durée de conservation des données">
          <ul>
            <li>
              <strong>Données de compte :</strong> conservées pendant toute la durée de
              l'abonnement actif, puis 3 ans après la résiliation
            </li>
            <li>
              <strong>Données comptables et de gestion :</strong> 10 ans conformément aux
              obligations légales (Code de commerce)
            </li>
            <li>
              <strong>Compte supprimé sur demande :</strong> suppression effective sous 30 jours
              (délai de rétractation)
            </li>
            <li>
              <strong>Données de navigation et cookies :</strong> 13 mois maximum
            </li>
          </ul>
        </Section>

        <Section title="Article 5 — Destinataires des données (sous-traitants)">
          <p>
            Les données personnelles des utilisateurs <strong>ne sont jamais vendues ni cédées</strong>{" "}
            à des tiers. Elles peuvent être transmises aux sous-traitants techniques suivants, dans
            le strict cadre de leur mission, encadrée par un contrat de sous-traitance conforme à
            l'article 28 du RGPD :
          </p>
          <ul>
            <li>
              <strong>Emergent</strong> (hébergement applicatif Kubernetes) — Union Européenne
            </li>
            <li>
              <strong>MongoDB Atlas</strong> (hébergement de la base de données) — Union Européenne,
              certifié SOC 2 + ISO 27001
            </li>
            <li>
              <strong>Stripe Payments Europe Ltd</strong> (traitement des paiements) — Irlande,
              certifié PCI DSS niveau 1
            </li>
            <li>
              <strong>Google Workspace (Gmail SMTP)</strong> (envoi des emails transactionnels) —
              Clauses Contractuelles Types CE
            </li>
            <li>
              <strong>Systeme.io</strong> (pages de vente externes et collecte de leads marketing) —
              Estonie / Union Européenne
            </li>
            <li>
              <strong>Qonto Payments SA</strong> (banque en ligne et plateforme agréée
              d'e-facturation — connexion optionnelle activée par l'utilisateur depuis ses
              paramètres) — France / Union Européenne, agréée par l'ACPR
            </li>
            <li>
              <strong>Meta Platforms Ireland Ltd</strong> (Meta Pixel pour la mesure d'audience
              publicitaire — uniquement après consentement) — Clauses Contractuelles Types CE
            </li>
            <li>
              <strong>Meta Ads (Facebook/Instagram Ads)</strong> (régie publicitaire pour la
              diffusion et le ciblage des campagnes — uniquement après consentement) — Meta
              Platforms Ireland Ltd, CCT
            </li>
            <li>
              <strong>OVH</strong> (bureau d'enregistrement du nom de domaine biz360.obusiness.fr et
              services associés) — France
            </li>
            <li>
              <strong>PostHog</strong> (analyse produit anonymisée — uniquement après consentement)
            </li>
            <li>
              <strong>Qonto</strong> (Olinda SAS, France) — Lecture des factures clients et
              fournisseurs depuis votre compte Qonto, <em>uniquement si vous avez explicitement
              activé l'intégration et consenti au partage</em>. Données concernées : numéro de
              facture, montant HT/TTC, date d'émission, statut de paiement, nom du
              client/fournisseur, pièces jointes (PDF). Accès en lecture seule, tokens chiffrés
              en base avec AES-128 (Fernet). Vous pouvez révoquer à tout moment depuis
              Paramètres → Déconnecter Qonto, ou directement depuis votre dashboard Qonto.
            </li>
          </ul>
          <p>
            Tous ces sous-traitants sont soumis à des obligations contractuelles de confidentialité
            et de sécurité conformes au RGPD.
          </p>
        </Section>

        <Section title="Article 6 — Droits des utilisateurs">
          <p>
            Conformément au RGPD, tout utilisateur dispose des droits suivants sur ses données
            personnelles :
          </p>
          <ul>
            <li>
              <strong>Droit d'accès :</strong> obtenir une copie des données vous concernant
            </li>
            <li>
              <strong>Droit de rectification :</strong> corriger des données inexactes ou
              incomplètes
            </li>
            <li>
              <strong>Droit à l'effacement :</strong> demander la suppression de vos données (sous
              réserve des obligations légales de conservation)
            </li>
            <li>
              <strong>Droit à la portabilité :</strong> recevoir vos données dans un format
              structuré et lisible (export ZIP disponible dans Paramètres → Mes données)
            </li>
            <li>
              <strong>Droit d'opposition :</strong> vous opposer à certains traitements fondés sur
              l'intérêt légitime
            </li>
            <li>
              <strong>Droit de retrait du consentement :</strong> à tout moment, pour les
              traitements fondés sur le consentement
            </li>
          </ul>
          <p>
            Pour exercer ces droits, contactez-nous à :{" "}
            <a
              href="mailto:t.samblat@gmail.com"
              className="underline"
              style={{ color: "#210875" }}
            >
              t.samblat@gmail.com
            </a>
            . Nous répondons sous 30 jours maximum.
          </p>
          <p>
            En cas de réclamation non résolue, vous pouvez saisir la{" "}
            <a
              href="https://www.cnil.fr"
              className="underline"
              style={{ color: "#210875" }}
              target="_blank"
              rel="noopener noreferrer"
            >
              CNIL
            </a>{" "}
            ou toute autre autorité de contrôle compétente.
          </p>
        </Section>

        <Section title="Article 7 — Sécurité des données">
          <p>
            Biz360 met en œuvre les mesures techniques et organisationnelles appropriées pour
            protéger les données contre tout accès non autorisé, perte, destruction ou divulgation :
          </p>
          <ul>
            <li>Connexions sécurisées par protocole HTTPS (TLS 1.2+)</li>
            <li>Mots de passe chiffrés par hachage bcrypt</li>
            <li>Tokens d'authentification JWT signés</li>
            <li>Chiffrement des données au repos (MongoDB Atlas)</li>
            <li>Sauvegardes automatiques quotidiennes</li>
            <li>Logs d'audit conservés pendant 6 mois</li>
          </ul>
          <p>
            En cas de violation de données susceptible d'engendrer un risque pour les droits et
            libertés des utilisateurs, nous nous engageons à notifier la CNIL dans les 72 heures
            réglementaires et à informer les utilisateurs concernés.
          </p>
        </Section>

        <Section title="Article 8 — Cookies et traceurs">
          <p>
            L'application Biz360 utilise deux catégories de cookies :
          </p>
          <p>
            <strong>8.1 Cookies essentiels (sans consentement requis)</strong>
          </p>
          <ul>
            <li>Authentification (JWT en localStorage)</li>
            <li>Préférences de session</li>
            <li>Mémorisation du choix de consentement cookies</li>
          </ul>
          <p>
            <strong>8.2 Cookies de mesure d'audience et publicitaires (consentement requis)</strong>
          </p>
          <ul>
            <li>
              <strong>Meta Pixel</strong> (Facebook/Instagram) — mesure de l'efficacité des
              campagnes publicitaires
            </li>
            <li>
              <strong>PostHog</strong> — analyse produit, parcours utilisateur anonymisé
            </li>
          </ul>
          <p>
            Ces cookies ne sont déposés <strong>qu'après votre consentement explicite</strong> via
            le bandeau affiché lors de votre première visite, conformément aux recommandations de
            la CNIL. Vous pouvez à tout moment modifier votre choix en effaçant les cookies de
            votre navigateur.
          </p>
        </Section>

        <Section title="Article 9 — Transferts hors Union Européenne">
          <p>
            Certains de nos sous-traitants (Meta, partiellement Google) peuvent transférer des
            données vers les États-Unis. Ces transferts sont encadrés par les{" "}
            <strong>Clauses Contractuelles Types</strong> de la Commission européenne et le Data
            Privacy Framework UE-US, garantissant un niveau de protection équivalent.
          </p>
        </Section>

        <Section title="Article 10 — Modifications de la politique">
          <p>
            La présente politique de confidentialité peut être mise à jour à tout moment. Les
            utilisateurs seront informés par e-mail ou notification dans l'application de toute
            modification substantielle. La date de dernière mise à jour est indiquée en tête de
            document.
          </p>
        </Section>

        <Section title="Article 11 — Droit applicable">
          <p>
            La présente politique est soumise au droit français. Tout litige relatif à la
            protection des données personnelles sera soumis à la compétence des juridictions
            françaises.
          </p>
        </Section>
      </main>

      <PublicFooter />
    </div>
  );
};

const Section = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="text-xl font-bold mb-3" style={{ color: "#210875" }}>
      {title}
    </h2>
    <div className="text-sm text-muted-foreground space-y-2 leading-relaxed [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1">
      {children}
    </div>
  </section>
);

export default PolitiqueConfidentialitePage;
